import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { dodRowEdits, draftRowsFrom, type GoalDodDraftRow, type GoalDodEdit, type GoalDodRow } from '../goal-dod-rows';
import { defineZenComponent } from './register';

export interface GoalDodRowsSaveDetail {
  edits: GoalDodEdit[];
}

/**
 * #2494: edits the DoD of a Goal whose `dod` copies its lens conditions, one
 * row per condition. A judgment's rows are edited like the others (#2537; a
 * current choice is held first when the principal says so); only time choice
 * rows are shown locked. The editor only builds the operations; the caller
 * saves them.
 */
function fitHeight(field: HTMLTextAreaElement) {
  field.style.height = 'auto';
  field.style.height = `${field.scrollHeight}px`;
}

class ZenGoalDodRowsEditor extends LitElement {
  static properties = {
    rows: { attribute: false },
    saving: { type: Boolean },
    draft: { state: true },
  };

  declare rows: GoalDodRow[];
  declare saving: boolean;
  declare draft: GoalDodDraftRow[];

  constructor() {
    super();
    this.rows = [];
    this.saving = false;
    this.draft = [];
  }

  createRenderRoot() {
    return this;
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('rows')) this.draft = draftRowsFrom(this.rows);
  }

  firstUpdated() {
    this.querySelector<HTMLTextAreaElement>('textarea.goal-dod-input')?.focus();
  }

  // A row is one sentence that may be longer than the field; grow the field to show all of it.
  updated() {
    this.querySelectorAll<HTMLTextAreaElement>('textarea.goal-dod-input').forEach(fitHeight);
  }

  private setText(index: number, text: string) {
    this.draft = this.draft.map((row, i) => i === index ? { ...row, text } : row);
  }

  private removeRow(index: number) {
    this.draft = this.draft.filter((_, i) => i !== index);
  }

  private addRow() {
    this.draft = [...this.draft, { condition_ref: null, text: '', judgment: false, locked: false }];
    void this.updateComplete.then(() => {
      const inputs = this.querySelectorAll<HTMLTextAreaElement>('textarea.goal-dod-input');
      inputs[inputs.length - 1]?.focus();
    });
  }

  private save() {
    if (this.saving) return;
    const edits = dodRowEdits(this.rows, this.draft);
    this.dispatchEvent(new CustomEvent<GoalDodRowsSaveDetail>('dod-rows-save', { detail: { edits }, bubbles: true }));
  }

  private cancel() {
    this.dispatchEvent(new CustomEvent('dod-rows-cancel', { bubbles: true }));
  }

  // The editor sits inside the clickable DoD section; its keys and clicks are its own.
  private onKeydown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') this.cancel();
  };

  render() {
    const hasLocked = this.draft.some((row) => row.locked);
    const hasJudgment = this.draft.some((row) => row.judgment && !row.locked);
    return html`
      <div class="goal-dod-rows-editor" @keydown=${this.onKeydown} @click=${(event: Event) => event.stopPropagation()}>
        ${this.draft.map((row, index) => row.locked
          ? html`<div class="goal-dod-row goal-dod-row-locked" title=${String(t('dodRowsJudgmentLocked'))}>
              <span class="mi mi-xs">lock</span><span class="goal-dod-row-text">${row.text}</span>
            </div>`
          : html`<div class="goal-dod-row">
              <textarea class="goal-dod-input" rows="1" .value=${row.text} ?disabled=${this.saving}
                aria-label=${String(t('dodLabel'))}
                @input=${(event: Event) => { const field = event.target as HTMLTextAreaElement; fitHeight(field); this.setText(index, field.value); }}
                @keydown=${(event: KeyboardEvent) => { if (event.key === 'Enter' && !event.isComposing) { event.preventDefault(); this.save(); } }}></textarea>
              <button class="goal-dod-row-delete" type="button" title=${String(t('dodRowsDelete'))} aria-label=${String(t('dodRowsDelete'))}
                ?disabled=${this.saving} @click=${() => this.removeRow(index)}><span class="mi mi-sm">delete</span></button>
            </div>`)}
        ${hasJudgment ? html`<div class="goal-dod-rows-hint"><span class="mi mi-xs">info</span> ${String(t('dodRowsJudgmentHint'))}</div>` : nothing}
        ${hasLocked ? html`<div class="goal-dod-rows-hint"><span class="mi mi-xs">info</span> ${String(t('dodRowsJudgmentLocked'))}</div>` : nothing}
        <div class="goal-dod-rows-actions">
          <button class="btn btn-sm" type="button" ?disabled=${this.saving} @click=${() => this.addRow()}><span class="mi mi-sm">add</span> ${String(t('dodRowsAdd'))}</button>
          <span class="goal-dod-rows-spacer"></span>
          <button class="btn btn-sm" type="button" ?disabled=${this.saving} @click=${() => this.cancel()}>${String(t('dodRowsCancel'))}</button>
          <button class="btn btn-sm btn-primary" type="button" ?disabled=${this.saving} @click=${() => this.save()}>${String(t('dodRowsSave'))}</button>
        </div>
      </div>
    `;
  }
}

defineZenComponent('zen-goal-dod-rows-editor', ZenGoalDodRowsEditor);
